import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Box, 
  Typography, 
  Chip,
  IconButton,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate } from 'react-router-dom';

export default function ExampleLetterModal({ letter, open, onClose }) {
  const navigate = useNavigate();

  const handleGetYours = () => {
    navigate('/register');
  };

  if (!letter) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1e293b',
          backgroundImage: 'none',
          border: '1px solid rgba(0, 229, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          bgcolor: '#0f172a',
          borderBottom: '1px solid rgba(0, 229, 255, 0.2)',
          pr: 6,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ fontSize: '2.5rem' }}>{letter.companyIcon}</Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ color: '#00e5ff', fontWeight: 600 }}>
              {letter.company}
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
              {letter.headline}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#94a3b8',
              '&:hover': { color: '#00e5ff' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 4 }}>
        {/* Success Result Badge */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            bgcolor: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <CheckCircleIcon sx={{ color: '#22c55e', fontSize: '2rem' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ color: '#22c55e', fontWeight: 600 }}>
              ✅ Result:
            </Typography>
            <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 0.5 }}>
              {letter.result}
            </Typography>
          </Box>
        </Box>

        {/* Legal Citations */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ color: '#94a3b8', mb: 1, fontWeight: 600 }}>
            📚 Laws Cited:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {letter.laws.map((law, idx) => (
              <Chip
                key={idx}
                label={law}
                size="small"
                sx={{
                  bgcolor: 'rgba(0, 229, 255, 0.1)',
                  color: '#00e5ff',
                  border: '1px solid rgba(0, 229, 255, 0.3)',
                  fontSize: '0.75rem'
                }}
              />
            ))}
          </Box>
        </Box>

        <Divider sx={{ my: 3, borderColor: 'rgba(0, 229, 255, 0.1)' }} />

        {/* Full Letter */}
        <Box
          sx={{
            p: 3,
            bgcolor: '#0f172a',
            border: '1px solid rgba(0, 229, 255, 0.1)',
            borderRadius: 1,
            maxHeight: '400px',
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: '#1e293b',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'rgba(0, 229, 255, 0.3)',
              borderRadius: '4px',
              '&:hover': {
                bgcolor: 'rgba(0, 229, 255, 0.5)',
              }
            }
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#e2e8f0',
              fontFamily: 'monospace',
              fontSize: '0.9rem',
              lineHeight: 1.8,
              whiteSpace: 'pre-line'
            }}
          >
            {letter.fullLetter}
          </Typography>
        </Box>

        {/* CTA Banner */}
        <Box
          sx={{
            mt: 3,
            p: 3,
            bgcolor: 'rgba(0, 229, 255, 0.1)',
            border: '2px solid #00e5ff',
            borderRadius: 2,
            textAlign: 'center'
          }}
        >
          <Typography variant="h6" sx={{ color: '#00e5ff', fontWeight: 600, mb: 1 }}>
            Want a Letter Like This?
          </Typography>
          <Typography variant="body2" sx={{ color: '#cbd5e1', mb: 2 }}>
            Generate your own professional, law-citing letter in 30 seconds
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleGetYours}
            sx={{
              bgcolor: '#00e5ff',
              color: '#0a0e17',
              fontWeight: 700,
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              '&:hover': {
                bgcolor: '#00c8e0',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s ease'
            }}
          >
            Get My Letter Free
          </Button>
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          p: 3,
          bgcolor: '#0f172a',
          borderTop: '1px solid rgba(0, 229, 255, 0.2)',
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          fullWidth
          sx={{
            borderColor: 'rgba(0, 229, 255, 0.3)',
            color: '#00e5ff',
            '&:hover': {
              borderColor: '#00e5ff',
              bgcolor: 'rgba(0, 229, 255, 0.1)',
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
